import { applyConsentToHubSpot } from './hubspot';
import { applyConsentToPostHog } from './posthog';

/**
 * Cookie-consent record and the machinery that turns a visitor's choice into
 * tracker configuration. Storage handling mirrors ./loginAttempt.ts: a
 * module-level key, try/catch around every access, and graceful degradation in
 * private mode.
 */

const KEY = 'b2b:cookie_consent';

/**
 * Bumped when the SHAPE of a stored record changes in a way that makes an old
 * record unsafe to honour. Distinct from the banner's published version (which
 * tracks copy changes) — either one being ahead re-shows the banner.
 */
export const CONSENT_SCHEMA_VERSION = 1;

export interface ConsentCategories {
  /** Always true — strictly-necessary storage carries no tracking. */
  necessary: true;
  /** Capture analytics events at all. */
  analytics: boolean;
  /** Build a durable, cross-visit profile and share it (CPRA sale/sharing). */
  sale: boolean;
}

export interface ConsentRecord {
  v: number;
  /** Banner publishedVersion the visitor decided against. */
  bannerVersion: number;
  decidedAt: number;
  categories: ConsentCategories;
  /** Whether a Global Privacy Control signal influenced the default. */
  gpc: boolean;
}

export const ACCEPT_ALL: ConsentCategories = {
  necessary: true,
  analytics: true,
  sale: true
};
export const REJECT_ALL: ConsentCategories = {
  necessary: true,
  analytics: false,
  sale: false
};

/** True when the browser is broadcasting a Global Privacy Control opt-out. */
export const hasGpcSignal = (): boolean => {
  try {
    return (
      typeof navigator !== 'undefined' &&
      (navigator as Navigator & { globalPrivacyControl?: boolean })
        .globalPrivacyControl === true
    );
  } catch {
    return false;
  }
};

/**
 * Validates every field downstream logic actually reads, not just `v` and
 * `categories.analytics`. A record missing e.g. `bannerVersion` would still
 * pass a narrower check, and `shouldShowBanner` would then compare
 * `undefined < publishedVersion` — which is `false` — silently suppressing
 * the banner and applying whatever partial consent survived instead of
 * re-prompting for a fresh, complete decision.
 */
export const readConsent = (): ConsentRecord | null => {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return null;
    const record = JSON.parse(raw) as ConsentRecord;
    const valid =
      typeof record?.v === 'number' &&
      typeof record.bannerVersion === 'number' &&
      typeof record.decidedAt === 'number' &&
      record.categories?.necessary === true &&
      typeof record.categories?.analytics === 'boolean' &&
      typeof record.categories?.sale === 'boolean';
    return valid ? record : null;
  } catch {
    return null;
  }
};

export const writeConsent = (record: ConsentRecord): void => {
  try {
    localStorage.setItem(KEY, JSON.stringify(record));
  } catch {
    // Private mode / disabled storage: the choice still applies for this page
    // load via applyConsentToPostHog; it just won't be remembered.
  }
};

export const buildConsentRecord = (
  categories: ConsentCategories,
  bannerVersion: number,
  gpc: boolean,
  now: number
): ConsentRecord => ({
  v: CONSENT_SCHEMA_VERSION,
  bannerVersion,
  decidedAt: now,
  categories,
  gpc
});

/**
 * Whether the banner must be shown, given the stored record and the currently
 * published banner version. Re-prompts when there is no record, when the schema
 * moved, or when the banner copy the visitor consented to has been republished.
 */
export const shouldShowBanner = (
  record: ConsentRecord | null,
  bannerEnabled: boolean,
  publishedVersion: number
): boolean => {
  if (!bannerEnabled) return false;
  if (!record) return true;
  if (record.v < CONSENT_SCHEMA_VERSION) return true;
  return record.bannerVersion < publishedVersion;
};

/**
 * Apply a stored/just-made decision to every tracker. Safe to call at boot.
 *
 * The single fan-out point, called from three places (src/index.tsx at boot, the
 * cookie banner, and the privacy-choices form). New trackers belong here, never
 * at those call sites, or a decision made on one surface silently won't reach
 * them.
 *
 * Note the two trackers do NOT treat these categories identically — HubSpot has
 * no anonymous mode, so it stays off for analytics-without-sale. See
 * `applyConsentToHubSpot`.
 */
export const applyConsent = (categories: ConsentCategories): void => {
  applyConsentToPostHog(categories);
  applyConsentToHubSpot(categories);
};

/**
 * The durable-profile gate PostHog's own opt-out can't express: even when
 * analytics is on, we only `identify()`/`group()` (which write a person profile)
 * if the visitor allowed sale/sharing. Read by AnalyticsBootstrap.
 */
export const canBuildDurableProfile = (): boolean =>
  readConsent()?.categories.sale === true;
